<script setup>
import { inject, ref } from "vue";
import AppLink from "@Components/AppLink.vue";
import { getType, formatMetrics } from "@Utils/project";
import { PencilIcon, TrashIcon } from "@heroicons/vue/20/solid";
import { useForm } from "@/form";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { PrimaryButton } from "@Components/Button";
import { FormInput, FormSwitch } from "@Components/Form";
import FormModal from "@Components/Modal/FormModal.vue";
import { useStore as useTemplatesStore } from "@/Stores/templates";
import ActionMessage from "@Components/ActionMessage.vue";
import Tooltip from "@Components/Tooltip.vue";
import DestroyModal from "@Components/Modal/DestroyModal.vue";
import { LayoutTemplateIcon } from "lucide-vue-next";

const props = defineProps(["project"]);
const emit = defineEmits(["updated"]);

const $notify = inject("$notify");
const { t: __ } = useI18n();
const templatesStore = useTemplatesStore();
const storeTemplateModal = ref();

const router = useRouter();

const enableForm = useForm({
    project: props.project.id,
});

const disableForm = useForm({
    project: props.project.id,
});

const destroyForm = useForm({
    project: props.project.id,
});

const storeTemplateForm = useForm({
    name: "",
    project: props.project.id,
});

const destroyModal = ref();

const enable = () => {
    if (enableForm.processing) return;
    enableForm
        .post("projects_enable")
        .then(() => {
            $notify({
                title: __("Le projet #{id} a été activé.", { id: enableForm.project }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while activation.",
                type: "error",
            });
        });
};

const disable = () => {
    if (disableForm.processing) return;

    disableForm
        .post("projects_disable")
        .then(() => {
            $notify({
                title: __("Le projet #{id} a été désactivé.", { id: disableForm.project }),
                type: "success",
            });

            emit("updated");
        })
        .catch(() => {
            $notify({
                title: "An error occurred while deactivating.",
                type: "error",
            });
        });
};

const destroy = () => {
    if (destroyForm.processing) return;
    destroyForm
        .post("projects_destroy")
        .then(() => {
            destroyModal.value.hide();

            $notify({
                title: __("Le projet #{id} a été supprimé.", { id: destroyForm.project }),
                type: "success",
            });

            router.push({ name: "projects.index" });
        })
        .catch(() => {
            destroyModal.value.hide();

            $notify({
                title: "An error occurred while deleting.",
                type: "error",
            });
        });
};

const storeTemplate = async () => {
    if (storeTemplateForm.processing) return;

    await storeTemplateForm.post("templates_store");

    templatesStore.fetch();
};
</script>

<template>
    <header>
        <div class="flex flex-col justify-between items-start gap-x-8 gap-y-4 px-4 py-4 lg:px-8 sm:flex-row sm:items-center sm:px-6">
            <div>
                <div>
                    <div class="flex items-center gap-x-3">
                        <FormSwitch :modelValue="project.enabled" @update:modelValue="(e) => (e ? enable() : disable())"></FormSwitch>

                        <h1 class="flex gap-x-3 text-base leading-7">
                            <span class="font-semibold text-gray-900 dark:text-white">{{ project.name }}</span>
                            <span class="text-gray-600 dark:text-gray-400">/</span>
                            <span class="font-semibold text-gray-900 dark:text-white">{{ __(getType(project.type)?.label) }}</span>
                            <div class="flex items-center">
                                <div
                                    v-if="project.is_complete"
                                    class="bg-green-500/10 ring-green-500/20 px-2 py-1 text-xs font-medium text-green-400 rounded-md ring-1 ring-inset">
                                    {{ __("Complété") }}
                                </div>
                                <div
                                    v-else
                                    class="bg-yellow-400/10 ring-yellow-400/20 px-2 py-1 text-xs font-medium text-yellow-400 rounded-md ring-1 ring-inset">
                                    {{ __("En cours") }}
                                </div>
                            </div>
                        </h1>
                    </div>
                    <div
                        v-if="['news', 'rss'].indexOf(project.type) !== -1 && project.modules.autopilot?.query"
                        class="mt-1.5 ml-14 text-xs text-gray-900 dark:text-white">
                        {{ project.modules.autopilot.query }}
                    </div>
                </div>
                <div class="flex items-center gap-x-2.5 mt-3 text-xs leading-5 text-gray-600 dark:text-gray-400">
                    <template v-if="project.persona">
                        <div class="flex items-center gap-x-4">
                            <img :src="project.persona.user_avatar_url" class="w-8 h-8 bg-gray-800 rounded-full dark:bg-gray-700" />
                            <div class="text-sm font-medium leading-6 truncate">{{ project.persona.user_full_name }}</div>
                        </div>
                    </template>
                </div>
            </div>

            <div class="w-full flex flex-none justify-between items-center gap-x-6 sm:w-auto">
                <PrimaryButton :as="AppLink" :to="{ name: 'projects.edit', params: { id: project.id } }" :icon="PencilIcon">
                    {{ __("Modifier") }}
                </PrimaryButton>
                <Tooltip :content="__('Créer un template à partir de ce projet')">
                    <div
                        @click="storeTemplateModal.show()"
                        class="inline-flex items-center text-gray-900 cursor-pointer select-none group dark:text-white hover:text-gray-950">
                        <LayoutTemplateIcon
                            class="w-5 h-5 text-gray-600 dark:text-gray-400 dark:group-hover:text-white group-hover:text-gray-800"
                            aria-hidden="true" />
                    </div>
                </Tooltip>
                <div
                    @click="destroyModal.show()"
                    class="inline-flex items-center text-gray-900 cursor-pointer select-none group dark:text-white hover:text-gray-950">
                    <TrashIcon class="w-5 h-5 text-gray-600 dark:text-gray-400 dark:group-hover:text-white group-hover:text-gray-800" aria-hidden="true" />
                </div>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-4 sm:grid-cols-2">
            <div
                v-for="(stat, statIdx) in formatMetrics(project.metrics)"
                :key="stat.name"
                :class="[
                    statIdx % 2 === 1 ? 'sm:border-l' : statIdx === 2 ? 'lg:border-l' : '',
                    'border-t border-gray-300 dark:border-gray-800 py-6 px-4 sm:px-6 lg:px-8',
                ]">
                <p class="text-sm font-medium leading-6 text-gray-600 dark:text-gray-400">{{ __(stat.name) }}</p>
                <p class="flex items-baseline gap-x-2 mt-2">
                    <span class="text-4xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ stat.value }}</span>
                    <span v-if="stat.unit" class="text-sm text-gray-600 dark:text-gray-400">{{ stat.unit }}</span>
                </p>
            </div>
        </div>

        <DestroyModal
            ref="destroyModal"
            :title="__('Supprimer le projet #{id}', { id: project.id })"
            :description="__('Êtes-vous sûr de vouloir supprimer le projet #{id} ? Cette action ne peut pas être annulée.', { id: project.id })"
            @destroy="destroy" />

        <FormModal ref="storeTemplateModal" @submit="storeTemplate" :title="__('Enregistrer un template')">
            <template #body>
                <FormInput :label="__('Nom du template')" v-model="storeTemplateForm.name" :errors="storeTemplateForm.getErrors('name')" :z="1" />
            </template>
            <template #actions>
                <div class="flex justify-end items-center">
                    <ActionMessage :on="storeTemplateForm.recentlySuccessful" class="mr-3"> {{ __("Sauvegardé.") }} </ActionMessage>
                    <PrimaryButton :processing="storeTemplateForm.processing" type="submit" :z="1">
                        {{ __("Sauvegarder le template") }}
                    </PrimaryButton>
                </div>
            </template>
        </FormModal>
    </header>
</template>
